// tempurpedic site footer rendered in-tree by PageSkeleton. The whole
// <footer> renders only when FooterSettings.site_footer has blocks.
//
// Footer NAV is resolved server-side from the footer NavMenu into a typed tree
// (chrome._build_footer_nav) and rendered here as a real React tree -- the
// desktop `.footer-nav` + mobile `.footer-nav-mobile` <nav> blocks, matching
// nav_menus/tags/menu.html (+ nav_category.html + menu_link.html). Each mobile
// section owns its collapse state, toggling `footer-nav-mobile__block--active`
// on click (MobileFooterBlock below).
import React from "react";
import { FooterWidgetsIsland as FooterWidgets } from "tsi-common-react/src/components/FooterWidgetsIsland/index";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { ServiceFooterIsland as ServiceFooter } from "tsi-common-react/src/components/ServiceFooterIsland/index";
import { t } from "ttag";

import { Context } from "@reactivated";

type IFooter = React.ContextType<typeof Context>["chrome"]["footer"];
type ISocialLink = IFooter["social_links"][number];
type IFooterNav = IFooter["footer_nav"];
type IFooterNavCategory = IFooterNav["columns"][number]["categories"][number];
type IFooterNavItem = IFooterNavCategory["items"][number];
// sealywarranty-only footer data; present iff the sealywarranty layout renders.
type ISealyFooter = NonNullable<IFooter["sealywarranty"]>;

// menu_link.html adds target/rel only on open-in-new-tab links, and preserves
// the legacy `rel="noopener nofollower"` spelling byte-for-byte. Untargeted
// links are left bare here: the shared links.ts widget sets target="_top" on
// them post-hydration (real DOM, after React commits -- no hydration mismatch),
// reproducing the runtime footer DOM the server-rendered markup ended up with.
const newTabAttrs = (
    item: IFooterNavItem,
): { target: "_blank"; rel: "noopener nofollower" } | Record<string, never> =>
    item.open_in_new_tab
        ? ({ target: "_blank", rel: "noopener nofollower" } as const)
        : ({} as const);

// A DOMContentLoaded title-fixup for the GCR iframe.
const GMERCHANT_SCRIPT = `
    window.addEventListener('DOMContentLoaded', function() {
        var googleMerchantIframe = document.querySelector('iframe[ng-non-bindable]');
        if (googleMerchantIframe != null) {
            googleMerchantIframe.title = 'Google Merchant';
        }
    });
`;

// Inline social icon SVGs keyed by SocialLink.icon slug. Each reproduces its
// svg-django source (same viewBox/dimensions/path) and receives `fill` from
// `.footer-social-list__icon` in _footer.scss, so the icon must be inline SVG
// rather than an <img>.
const SOCIAL_ICON_SVGS: Record<string, React.JSX.Element> = {
    youtube: (
        <svg
            className="footer-social-list__icon footer-social-list__icon--youtube"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <title>Tempur-pedic YouTube</title>
            <path d="M1280 896q0-37-30-54L738 522q-31-20-65-2-33 18-33 56v640q0 38 33 56 16 8 31 8 20 0 34-10l512-320q30-17 30-54m512 0q0 96-1 150t-8.5 136.5T1760 1330q-16 73-69 123t-124 58q-222 25-671 25t-671-25q-71-8-124.5-58T31 1330q-14-65-21.5-147.5T1 1046 0 896t1-150 8.5-136.5T32 462q16-73 69-123t124-58q222-25 671-25t671 25q71 8 124.5 58t69.5 123q14 65 21.5 147.5T1791 746t1 150" />
        </svg>
    ),
    facebook: (
        <svg
            className="footer-social-list__icon footer-social-list__icon--facebook"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <title>Tempur-pedic Facebook</title>
            <path d="M1343 12v264h-157q-86 0-116 36t-30 108v189h293l-39 296h-254v759H734V905H479V609h255V391q0-186 104-288.5T1115 0q147 0 228 12" />
        </svg>
    ),
    twitter: (
        <svg
            className="footer-social-list__icon footer-social-list__icon--twitter"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            xmlSpace="preserve"
            viewBox="0 0 300 271"
        >
            <title>Tempur-pedic X</title>
            <path
                d="M236 0h46L181 115l118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123L-1.1 0h94.9l65.5 86.6zm-16.1 244h25.5L80.4 26H53z"
                className="st0"
            />
        </svg>
    ),
    pinterest: (
        <svg
            className="footer-social-list__icon footer-social-list__icon--pinterest"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <title>Tempur-pedic Pinterest</title>
            <path d="M256 597q0-108 37.5-203.5T397 227t152-123 185-78T936 0q158 0 294 66.5T1451 260t85 287q0 96-19 188t-60 177-100 149.5-145 103-189 38.5q-68 0-135-32t-96-88q-10 39-28 112.5t-23.5 95-20.5 71-26 71-32 62.5-46 77.5-62 86.5l-14 5-9-10q-15-157-15-188 0-92 21.5-206.5T604 972t52-203q-32-65-32-169 0-83 52-156t132-73q61 0 95 40.5T937 514q0 66-44 191t-44 187q0 63 45 104.5t109 41.5q55 0 102-25t78.5-68 56-95 38-110.5 20-111 6.5-99.5q0-173-109.5-269.5T909 163q-200 0-334 129.5T441 621q0 44 12.5 85t27 65 27 45.5T520 847q0 28-15 73t-37 45q-2 0-17-3-51-15-90.5-56t-61-94.5-32.5-108T256 597" />
        </svg>
    ),
    instagram: (
        <svg
            className="footer-social-list__icon footer-social-list__icon--instagram"
            role="img"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            width="1792"
            height="1792"
            viewBox="0 0 1792 1792"
        >
            <title>Tempur-pedic Instagram</title>
            <path d="M1490 1426V778h-135q20 63 20 131 0 126-64 232.5T1137 1310t-240 62q-197 0-337-135.5T420 909q0-68 20-131H299v648q0 26 17.5 43.5T360 1487h1069q25 0 43-17.5t18-43.5m-284-533q0-124-90.5-211.5T897 594q-127 0-217.5 87.5T589 893t90.5 211.5T897 1192q128 0 218.5-87.5T1206 893m284-360V368q0-28-20-48.5t-49-20.5h-174q-29 0-49 20.5t-20 48.5v165q0 29 20 49t49 20h174q29 0 49-20t20-49m174-208v1142q0 81-58 139t-139 58H325q-81 0-139-58t-58-139V325q0-81 58-139t139-58h1142q81 0 139 58t58 139" />
        </svg>
    ),
};

// One social link: a screen-reader name span plus the inline SVG selected by
// link.icon, wrapped in the anchor.
const SocialIcon = ({ link }: { link: ISocialLink }): React.JSX.Element => (
    <li className="footer-social-list__item">
        <a
            title={link.name}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
        >
            <span className="ada-screenreader-only">{link.name}</span>
            {SOCIAL_ICON_SVGS[link.icon]}
        </a>
    </li>
);

export const FooterNavigation = ({
    nav,
}: {
    nav: IFooterNav;
}): React.JSX.Element => {
    return (
        <>
            {/* Desktop: one `.footer-nav__col` per footer_column block, its
                `.footer-nav__list` holding every category's header + items
                (nav_category.html + menu_link.html). Raw-HTML sub-blocks (e.g.
                the OneTrust cookie-settings button) render in place as their
                stored markup. */}
            <nav
                className="footer-nav l-capped-width"
                data-site={nav.site_hostname}
                data-menu-name={nav.menu_name}
                data-menu-calling-page={nav.calling_page}
                data-menu-len={nav.menu_len}
            >
                {nav.columns.map((column, ci) => (
                    <div className="footer-nav__col" key={ci}>
                        <ul className="footer-nav__list">
                            {column.categories.map((category, cati) => (
                                <React.Fragment key={cati}>
                                    <li className="site-footer__header">
                                        <h2>{category.title}</h2>
                                    </li>
                                    {category.items.map((item, li) =>
                                        item.block_type === "html" ? (
                                            // Raw CMS HTML (RawHTMLBlock /
                                            // nav_footer). RawHTMLBlock strips
                                            // the pre-wrapped raw-html-block div
                                            // so the host IS that element
                                            // (matching the legacy DOM). An
                                            // externally-managed subtree
                                            // (OneTrust mutates its button
                                            // pre-hydration), so suppress the
                                            // mismatch warning.
                                            <RawHTMLBlock
                                                key={li}
                                                value={item.html}
                                                suppressHydrationWarning
                                            />
                                        ) : (
                                            <li
                                                className="footer-nav__item"
                                                key={li}
                                            >
                                                <a
                                                    className="footer-nav__item-link"
                                                    href={item.url}
                                                    {...newTabAttrs(item)}
                                                >
                                                    {item.label}
                                                </a>
                                            </li>
                                        ),
                                    )}
                                </React.Fragment>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            {/* Mobile: one `.footer-nav-mobile__block` per category, flattened
                across columns. menu.html never sets target/rel on the mobile
                links (even open-in-new-tab ones), so they stay bare here.
                Raw-HTML sub-blocks render in place inside the dropdown. */}
            <nav
                className="footer-nav-mobile u-margin-bottom--xl"
                data-site={nav.site_hostname}
                data-menu-name={nav.menu_name}
                data-menu-calling-page={nav.calling_page}
                data-menu-len={nav.menu_len}
            >
                {nav.columns.map((column, ci) =>
                    column.categories.map((category, cati) => (
                        <MobileFooterBlock
                            key={`${ci}-${cati}`}
                            category={category}
                        />
                    )),
                )}
            </nav>
        </>
    );
};

// One collapsible mobile footer section: clicking the header toggles
// `footer-nav-mobile__block--active` on the `<dl>` (component state).
const MobileFooterBlock = ({
    category,
}: {
    category: IFooterNavCategory;
}): React.JSX.Element => {
    const [active, setActive] = React.useState(false);
    return (
        <dl
            className={
                active
                    ? "footer-nav-mobile__block footer-nav-mobile__block--active"
                    : "footer-nav-mobile__block"
            }
        >
            <dt
                className="footer-nav-mobile__header site-footer__header"
                role="button"
                tabIndex={0}
                aria-expanded={active}
                onClick={() => {
                    setActive((value) => !value);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActive((value) => !value);
                    }
                }}
            >
                {category.title}
            </dt>
            <div className="site-footer__dropdown">
                {category.items.map((item, li) =>
                    item.block_type === "html" ? (
                        <RawHTMLBlock
                            key={li}
                            value={item.html}
                            suppressHydrationWarning
                        />
                    ) : (
                        <dd
                            className="footer-nav__item footer-nav__item--mobile"
                            key={li}
                        >
                            <a
                                className="footer-nav__item--mobile-link"
                                href={item.url}
                            >
                                {item.label}
                            </a>
                        </dd>
                    ),
                )}
            </div>
        </dl>
    );
};

const FooterBadges = ({ footer }: { footer: IFooter }): React.JSX.Element => (
    <>
        {footer.show_badges && (
            <div className="footer-badges l-capped-width">
                <div className="footer-badges__item footer-badges__item--a360">
                    <a
                        href="https://allyant.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <img
                            className="responsive-img"
                            src={footer.a360_badge_url}
                            alt={t`An Accessible360 badge sharing that we've partnered with an industry-leading ADA vendor who maintains our site is in good standing with accessibility.`}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                </div>
            </div>
        )}
        <script dangerouslySetInnerHTML={{ __html: GMERCHANT_SCRIPT }} />
    </>
);

const FooterLegalDisclaimers = ({
    footer,
}: {
    footer: IFooter;
}): React.JSX.Element => (
    <div className="footer-legal l-capped-width">
        <div className="footer-legal__copyright">
            <ul className="footer-legal__list">
                <li className="footer-legal__item">
                    &copy; {footer.copyright_year} {footer.legal_text}{" "}
                    {t`All Rights Reserved.`}
                </li>
                <li className="footer-legal__item">
                    <a
                        title={t`Privacy Policy`}
                        href={footer.privacy_policy_url}
                    >
                        {t`Privacy Policy`}
                    </a>
                </li>
                <li className="footer-legal__item">
                    <a title={t`Terms of Use`} href={footer.terms_url}>
                        {t`Terms of Use`}
                    </a>
                </li>
            </ul>
            <ul className="footer-social-list">
                {footer.social_links.map((link, i) => (
                    <SocialIcon key={i} link={link} />
                ))}
            </ul>
        </div>
        <div className="footer-legal__aux-copy">
            {footer.footer_copy.map((item, i) => (
                <div key={i} dangerouslySetInnerHTML={{ __html: item.html }} />
            ))}
        </div>
    </div>
);

// Per-platform social link labels, localized client-side with ttag.
// Keyed on the generated platform union so missing entries fail at compile time.
type ISealyPlatform = ISealyFooter["social_links"][number]["platform"];
const SEALY_SOCIAL_COPY: Record<
    ISealyPlatform,
    { title: string; alt: string }
> = {
    facebook: { title: t`Sealy on Facebook`, alt: t`Facebook icon` },
    twitter: { title: t`Sealy on Twitter`, alt: t`Twitter icon` },
    youtube: { title: t`Sealy on YouTube`, alt: t`YouTube icon` },
    instagram: { title: t`Sealy on Instagram`, alt: t`Instagram icon` },
    pinterest: { title: t`Sealy on Pinterest`, alt: t`Pinterest icon` },
};

const SealyWarrantyFooterLegalDisclaimers = ({
    footer,
    sealy,
}: {
    footer: IFooter;
    sealy: ISealyFooter;
}): React.JSX.Element => (
    <div className="footer-legal l-capped-width">
        <div className="footer__signup">
            <nav className="footer__social">
                {sealy.social_links.map((link, i) => {
                    const copy = SEALY_SOCIAL_COPY[link.platform];
                    return (
                        <a
                            key={i}
                            className="footer__social-item"
                            href={link.url}
                            title={copy.title}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <img
                                className={`footer__social-item--${link.platform} img-responsive`}
                                src={link.img_url}
                                alt={copy.alt}
                                loading="lazy"
                                decoding="async"
                            />
                        </a>
                    );
                })}
            </nav>
        </div>
        <div className="footer__badge">
            <div>
                <img
                    className="footer__badge responsive-img"
                    src={sealy.badge_url}
                    alt={t`Sealy, America's #1 mattress brand`}
                    loading="lazy"
                    decoding="async"
                />
            </div>
        </div>
        <div className="footer__copyright-legal">
            <div className="footer-legal__aux-copy">
                {footer.footer_copy.map((item, i) => (
                    <div
                        key={i}
                        dangerouslySetInnerHTML={{ __html: item.html }}
                    />
                ))}
            </div>
            <div className="footer__copyright">
                &copy; {footer.copyright_year} {footer.legal_text}
            </div>
            <ul className="footer-legal__list">
                <li className="footer-legal__item">
                    <a title={t`Terms of Use`} href={footer.terms_url}>
                        {t`Terms of Use`}
                    </a>
                </li>
                <li className="footer-legal__item">
                    <a
                        title={t`Privacy Policy`}
                        href={footer.privacy_policy_url}
                    >
                        {t`Privacy Policy`}
                    </a>
                </li>
            </ul>
        </div>
    </div>
);

type IProps = {
    footer: IFooter;
};

// Shared warranty footer shell (nav + disclaimers child + optional
// ServiceFooter; no widgets/badges). A 2nd warranty subsite (stearnswarranty,
// restored separately) swaps only the disclaimers child. FooterBadges + its GCR
// fixup (GMERCHANT_SCRIPT) are omitted, matching prod's warranty footer-badges
// overrides.
const WarrantySiteFooter = ({
    footer,
    children,
}: {
    footer: IFooter;
    children: React.ReactNode;
}): React.JSX.Element => (
    <footer className="site-footer l-full-width footer" aria-label={t`Footer`}>
        <FooterNavigation nav={footer.footer_nav} />
        {children}
        {footer.show_service_footer && (
            <ServiceFooter
                snippets={footer.service_footer_snippets_json}
                interval={footer.service_footer_interval}
                loops={footer.service_footer_loops}
            />
        )}
    </footer>
);

export const SiteFooter = (props: IProps): React.JSX.Element | null => {
    const { footer } = props;
    if (footer.site_footer_block_types.length === 0) {
        return null;
    }
    // Sealy Warranty uses a fixed footer layout. Unlike the default footer,
    // `site_footer_block_types` only controls whether the footer is rendered, not
    // its composition or block order.
    if (footer.sealywarranty) {
        return (
            <WarrantySiteFooter footer={footer}>
                <SealyWarrantyFooterLegalDisclaimers
                    footer={footer}
                    sealy={footer.sealywarranty}
                />
            </WarrantySiteFooter>
        );
    }
    return (
        <footer className="site-footer l-full-width">
            <FooterWidgets
                phone_number={footer.widgets.phone_number}
                show_contact_support={footer.widgets.show_contact_support}
                show_nearby_stores={footer.widgets.show_nearby_stores}
                chat_offline_link={footer.widgets.chat_offline_link}
            />
            {footer.site_footer_block_types.map((blockType, i) => {
                if (blockType === "site_footer_navigation") {
                    return <FooterNavigation key={i} nav={footer.footer_nav} />;
                }
                if (blockType === "site_footer_badges") {
                    return <FooterBadges key={i} footer={footer} />;
                }
                if (blockType === "site_footer_legal_disclaimers") {
                    return <FooterLegalDisclaimers key={i} footer={footer} />;
                }
                return null;
            })}
            {footer.show_service_footer && (
                <ServiceFooter
                    snippets={footer.service_footer_snippets_json}
                    interval={footer.service_footer_interval}
                    loops={footer.service_footer_loops}
                />
            )}
        </footer>
    );
};
